import React, { useContext, useEffect } from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { ToastContainer } from "react-toastify";

import AuthContext from "../context/AuthContext";

import Navbar from "../components/Navbar";
import ChatWidget from "../components/ChatWidget/ChatWidget";

import Home from "../pages/Home/Home";
import Register from "../pages/Auth/Register";
import MyProfile from "../pages/Profile/MyProfile";
import FriendsList from "../pages/Friends/FriendsList";
import PublicProfile from "../pages/Profile/PublicProfile";
import AdminDashboard from "../pages/AdminDashboard/AdminDashboard";
import CreateAvatarItem from "../pages/AdminDashboard/CreateAvatarItem";
import AdminImageDeletion from "../pages/AdminDashboard/AdminImageDeletion";
import ManageUsers from "../pages/AdminDashboard/ManageUsers";
import Avatars from "../pages/Avatars/Avatars";
import SearchUsers from "../components/SearchUsers";
import GroupsDashboard from "../pages/Groups/GroupsDashboard";
import ChangePassword from "../pages/Auth/ChangePassword";
import CalendarPage from "../pages/Calendar/CalendarPage";
import ModeratorDashboard from "../pages/AdminDashboard/ModerationDashboard";
import GlobalFeed from "../pages/Feed/GlobalFeed";
import Requests from '../pages/Friends/Requests';
import Settings from "../pages/Settings/Settings";

import {
    RequireAuth,
    RedirectIfAuthed,
    RequireRole,
} from "./RouteGuards";

import "react-toastify/dist/ReactToastify.css";
import "../styles/ToastStyles.css";
import "../AppTheme.css";

// ✅ Added 'moderation' to RESERVED list to prevent users from taking that username
const RESERVED = new Set([
    "login", "register", "profile", "friends", "edit-profile",
    "admin", "search", "user", "avatars", "groups",
    "calendar", "settings", "feed", "requests", "change-password", "moderation"
]);

const ConditionalPublicProfile = () => {
    const { pathname } = useLocation();
    const first = (pathname.split("/")[1] || "").toLowerCase();

    console.log(`🔀 [ConditionalPublicProfile] Checking path: "${pathname}"`);

    if (RESERVED.has(first)) {
        console.warn(`⛔ [ConditionalPublicProfile] "${first}" is reserved. Redirecting to Home.`);
        return <Navigate to="/" replace />;
    }

    console.log(`✅ [ConditionalPublicProfile] Rendering profile for user: "${first}"`);
    return <PublicProfile />;
};

const AppRoutes = () => {
    const { user, loading } = useContext(AuthContext);
    const location = useLocation();

    // 🔍 DEBUG LOGS
    useEffect(() => {
        console.group("📍 [AppRoutes] Navigation Event");
        console.log("URL Path:", location.pathname);
        console.log("Auth User:", user ? user.username : "Guest (null)");
        console.groupEnd();
    }, [location.pathname, user, loading]);

    // ✅ Define routes where the ChatWidget should be hidden
    const isAdminRoute = location.pathname.startsWith("/admin");
    const isModRoute = location.pathname.startsWith("/moderation");

    const hideWidget =
        isAdminRoute ||
        isModRoute ||
        location.pathname === "/register" ||
        location.pathname === "/login" ||
        location.pathname === "/";

    return (
        <>
            <Navbar />

            <Routes>
                {/* ✅ Public Pages */}
                <Route path="/" element={<Home />} />
                <Route path="/login" element={<Navigate to="/" replace />} />

                <Route
                    path="/register"
                    element={
                        <RedirectIfAuthed>
                            <Register />
                        </RedirectIfAuthed>
                    }
                />

                <Route path="/user/:username" element={<PublicProfile />} />
                <Route path="/search" element={<SearchUsers />} />

                {/* ✅ Protected Pages */}
                <Route path="/groups" element={<RequireAuth><GroupsDashboard /></RequireAuth>} />
                <Route path="/profile" element={<RequireAuth><MyProfile /></RequireAuth>} />
                <Route path="/feed" element={<RequireAuth><GlobalFeed /></RequireAuth>} />
                <Route path="/friends" element={<RequireAuth><FriendsList /></RequireAuth>} />
                <Route path="/calendar" element={<RequireAuth><CalendarPage /></RequireAuth>} />
                <Route path="/avatars" element={<RequireAuth><Avatars /></RequireAuth>} />
                <Route path="/change-password" element={<RequireAuth><ChangePassword /></RequireAuth>} />
                <Route path="/requests" element={<RequireAuth><Requests /></RequireAuth>} />
                <Route path="/settings" element={<RequireAuth><Settings /></RequireAuth>} />

                {/* 🛡️ Admin Routes */}
                <Route path="/admin" element={<RequireRole minRole="admin"><AdminDashboard /></RequireRole>} />
                <Route path="/admin/create-avatar-item" element={<RequireRole minRole="admin"><CreateAvatarItem /></RequireRole>} />
                <Route path="/admin/images" element={<RequireRole minRole="admin"><AdminImageDeletion /></RequireRole>} />
                <Route path="/admin/users" element={<RequireRole minRole="admin"><ManageUsers /></RequireRole>} />

                {/* 🛡️ Moderation Route - ✅ Updated minRole to 'moderator' so admins/owners can also enter */}
                <Route path="/moderation" element={<RequireRole minRole="moderator"><ModeratorDashboard /></RequireRole>} />

                {/* Catch All */}
                <Route path="/:username" element={<ConditionalPublicProfile />} />
                <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>

            {/* ✅ Widget Logic: Hidden if user is not logged in OR if hideWidget is true */}
            {user && !hideWidget && <ChatWidget />}

            <ToastContainer position="top-right" autoClose={3500} newestOnTop />
        </>
    );
};

export default AppRoutes;